import { HTMLAttributes, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { CasAutoCompleteAsync } from 'src/compass/shared/components';
import infoUriFromEntity from 'src/compass/shared/util/infoUriFromEntity';

import { ICustomerSearchItem, itemTypeStyle, typeChipSx } from './customerSearchItem';
import ContactDialog from './ContactDialog';
import CustomerDialog from './CustomerDialog';
import InactiveChip from './InactiveChip';
import { TOUCHPOINT_ENTITY } from './TouchpointsTab';
import UnassignedResultDialog from './UnassignedResultDialog';

export const CUSTOMER_SEARCH_URL = 'Utilities/CustomerSearch/List';

const chipSx = { textTransform: 'uppercase', fontSize: 10 } as const;

type OpenDialog =
  | { kind: 'customer'; id: number; tab?: 'Accounts' }
  | { kind: 'contact'; id: number }
  | { kind: 'unassigned'; item: ICustomerSearchItem };

/*
  The dialog a picked row opens. Customers open the Customers tab's own dialog; an account opens
  its customer's, on the Accounts tab; a contact opens its customer's when it has exactly one,
  and otherwise a Contact dialog listing the customers it has (or saying it has none). A HubSpot
  company, and an account no customer can be found for, have no customer to open, so they open a
  dialog saying so.
*/
function dialogFor(item: ICustomerSearchItem): OpenDialog {
  if (item.itemType === 'Customer') return { kind: 'customer', id: item.itemId };

  if (item.itemType === 'Company') return { kind: 'unassigned', item };

  if (item.itemType === 'Contact')
    return item.customerId
      ? { kind: 'customer', id: item.customerId }
      : { kind: 'contact', id: item.itemId };

  return item.customerId
    ? { kind: 'customer', id: item.customerId, tab: 'Accounts' }
    : { kind: 'unassigned', item };
}

/*
  Search across customers, contacts and accounts at once, from the Customers page title
  line. What the entry means is decided server-side (5 digits is an account number, any
  other text is a name), so this only has to render what comes back and open what is picked.
*/
export default function CustomerSearchBox() {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState<OpenDialog | null>(null);
  /*
    Bumped to remount the box once a pick has been handled. Re-picking the row it already
    holds raises no change event, so without this the same result can only be opened once.
  */
  const [boxKey, setBoxKey] = useState(0);

  const resetBox = () => setBoxKey((key) => key + 1);

  /*
    A customer dialog edits what the Customers tab lists (tags, frequency, next touchpoint), and
    that tab refreshes itself only for the dialog it opened. Its query is keyed by its URL first.
  */
  const refreshCustomersList = () =>
    queryClient.invalidateQueries({ queryKey: [infoUriFromEntity(TOUCHPOINT_ENTITY)] });

  const onPick = (item: ICustomerSearchItem | null) => {
    if (item) setOpenDialog(dialogFor(item));
  };

  const onDialogClose = () => {
    setOpenDialog(null);
    resetBox();
  };

  const renderOption = (
    liProps: HTMLAttributes<HTMLLIElement>,
    option: ICustomerSearchItem
  ) => {
    const { chipColor, textColor } = itemTypeStyle(option.itemType);

    return (
      <li {...liProps} key={option.id}>
        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          {/* Wraps rather than truncates: an ellipsized name is exactly what the user
              came here to read. */}
          <Typography variant="body2" color={textColor}>
            {option.name}
            {option.multipleCustomers && <i> (multiple customers)</i>}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', flexShrink: 0 }}>
            {option.inactive && <InactiveChip sx={chipSx} />}
            <Chip
              size="small"
              variant="soft"
              color={chipColor}
              label={option.typeLabel}
              sx={[chipSx, typeChipSx(option.typeLabel)]}
            />
          </Stack>
        </Stack>
      </li>
    );
  };

  return (
    <>
      <CasAutoCompleteAsync<ICustomerSearchItem>
        key={boxKey}
        url={CUSTOMER_SEARCH_URL}
        label="Enter cust, contact or acct name  - or acct#"
        width={520}
        size="small"
        /*
          The options already are the server's answer for this input, so MUI's own
          filtering can only take rows away - an account matched on its 5 digit
          number has nothing in its name for the input text to match.
        */
        filterOptions={(options) => options}
        renderOption={renderOption}
        onValueChange={(_, value) => onPick(value)}
      />
      {openDialog?.kind === 'customer' && (
        <CustomerDialog
          id={openDialog.id}
          defaultTab={openDialog.tab}
          onClose={(dataUpdated?: boolean) => {
            onDialogClose();
            if (dataUpdated) refreshCustomersList();
          }}
        />
      )}
      {openDialog?.kind === 'contact' && (
        <ContactDialog
          id={openDialog.id}
          onClose={onDialogClose}
          onCustomerUpdated={refreshCustomersList}
        />
      )}
      {openDialog?.kind === 'unassigned' && (
        <UnassignedResultDialog item={openDialog.item} onClose={onDialogClose} />
      )}
    </>
  );
}
